import React from "react";
import { useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import BSL_Dialog from "../../components/BSL_Dialog";
import * as BSL_Common from "../../components/BSL_CommonControls";
import { IconButton } from "@material-ui/core";
import { Delete as DeleteIcon, Close as CloseIcon } from "@material-ui/icons";
import { Palette } from "../../AppConfig";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";

export function isShortenedCode(productCode) {
    return /(^|[^#])##([^#]|$)/.test(productCode);
}

export function isMultiColouredCode(productCode) {
    // Find every "**" sequence; if there are ≥ 2, it’s multi-coloured
    const allMatches = productCode.match(/\*\*/g);
    return Array.isArray(allMatches) && allMatches.length >= 2;
}

export function checkValidJSON(str) {
    try {
        JSON.parse(str);
    } catch (e) {
        return false;
    }
    return true;
}

export function setDynamicsCode(matchedItems, rowKey, fieldName, newValue) {
    // 1. Shallow‐clone the top‐level array so we don’t mutate state directly
    const updated = [...matchedItems];

    // 2. Find the index of the object whose lyvesCode matches rowKey
    const idx = updated.findIndex((item) => item.lyvesCode === rowKey);

    if (idx !== -1) {
        // 3a. If found, shallow‐clone that object and overwrite only fieldName
        updated[idx] = {
            ...updated[idx],
            dynamicsCode: newValue,
        };
    } else {
        // 3b. If not found, push a brand‐new object with lyvesCode + the new field
        updated.push({
            lyvesCode: rowKey,
            dynamicsCode: newValue,
        });
    }

    // 4. Return the new array
    return updated;
}

// Builds expected lyves code
export function lyvesCodeBuilder(productCode, key, length) {
    if (!productCode) return "";

    // Make a mutable copy
    let resolvedCode = productCode;

    const isShortened = isShortenedCode(productCode);
    const isMultiColoured = isMultiColouredCode(productCode);
    const isDeg = productCode.includes("@");

    // Predeclare firstPart/secondPart so they exist in both branches:
    let firstPart = "";
    let secondPart = "";

    if (isMultiColoured) {
        // If the key is e.g. "ABCD", we take "AB" then "CD"
        firstPart = key.slice(0, 2);
        secondPart = key.slice(2, 4);

        // Replace the first "**" with firstPart, then the next "**" with secondPart
        resolvedCode = resolvedCode.replace("**", firstPart).replace("**", secondPart);
    } else {
        // If not multi‐coloured, we just replace the first "**" with the entire 2‐char key
        firstPart = key.slice(0, 2);
        resolvedCode = resolvedCode.replace("**", firstPart);
    }

    // If we also need to fill in a "##" because the code is shortened:
    if (isShortened && length !== undefined && length !== null && !isDeg) {
        // Ensure a 2‐digit string (e.g. 5 → "05", 12 → "12")
        const shortLength = String(length).padStart(2, "0").slice(0, 2);
        resolvedCode = resolvedCode.replace("##", shortLength);
    }

    if (length !== undefined && length !== null && isDeg) {
        resolvedCode = resolvedCode.replace("@@", length);
    }

    return resolvedCode;
}

export function extractLengths(promptcodes) {
    const matches = [...(promptcodes || "").matchAll(/(\d+)\(\d+\)/g)];
    const lengths = matches.map((m) => m[1]);
    return [...new Set(lengths.map(Number))];
}

export function extractPitch(promptcodes) {
    const match = (promptcodes || "").match(/@@=([\d|]+)/);
    if (!match) return [];

    const values = match[1].split("|").map(Number);
    return [...new Set(values)];
}

export function buildWZColumns(promptLengths, isDeg) {
    const base = [
        {
            id: "Key",
            label: "Colours",
            style: {
                width: "100px",
                position: "sticky",
                left: "0",
                zIndex: "3",
                fontWeight: "600",
            },
        },
    ];
    const hasColumnId = (arr, id) => arr.some((col) => col.id === id);

    if (promptLengths.length > 0) {
        promptLengths.forEach((length) => {
            const lengthStr = length.toString();
            if (!hasColumnId(base, lengthStr)) {
                base.push({
                    id: lengthStr,
                    label: lengthStr,
                    grouping: isDeg ? "Pitch" : "Length",
                    style: { width: "100px" },
                });
            }
        });
    } else {
        if (!hasColumnId(base, "DefaultLengthCode")) {
            base.push({
                id: "DefaultLengthCode",
                label: "default",
                grouping: isDeg ? "Pitch" : "Length",
                style: { width: "100px" },
            });
        }
    }

    return base;
}

export function getKeys(row, materials) {
    if (!row?.ProductCode.includes("*")) return ["DEFAULT"];
    const colour_map = materials?.colour_map;
    const colour_arrays = materials?.colour_arrays;
    const colours_string = row?.Colours || "Colorbond";
    const keys = [];

    if (colour_arrays?.[colours_string]) {
        colour_arrays[colours_string].forEach((colour) => {
            const code = colour_map[colour]?.Code;
            if (code != null) {
                keys.push(code);
            }
        });
    } else if (colours_string?.includes(",")) {
        colours_string.split(",").forEach((colour) => keys.push(colour_map?.[colour]?.Code) || []);
    }

    return keys;
}

export function deleteExtra(matchedItems, d365CodeToDelete) {
    const data = { ...matchedItems };
    delete data[d365CodeToDelete];
    return data;
}

export function findBestMatch(items, key, columnId, productCode) {
    const wildcardIndexRaw = productCode.indexOf("*");
    const wildcardIndex = wildcardIndexRaw >= 0 ? wildcardIndexRaw : 0;

    const isShortened = productCode.includes("##");
    const isMultiColoured = (productCode.match(/\*\*/g) || []).length >= 2;
    const hasSingleDoubleStar = productCode.includes("**") && !isMultiColoured;

    let resolvedCode = productCode;

    // Handle ## replacement
    if (isShortened) {
        const shortColumnId = columnId.slice(0, 2);
        resolvedCode = resolvedCode.replace("##", shortColumnId);
    }

    // Handle @@ replacement
    if (productCode.includes("@")) {
        resolvedCode = resolvedCode.replace("@@", columnId);
    }

    // Handle true multi-coloured codes like **H** or ****
    if (isMultiColoured) {
        const firstPart = key.slice(0, 2);
        const secondPart = key.slice(2, 4);
        resolvedCode = resolvedCode.replace("**", firstPart).replace("**", secondPart);
    }

    // Handle single ** replacement like CNCFL01**
    if (hasSingleDoubleStar) {
        resolvedCode = resolvedCode.replace("**", key);
    }

    // Attempt exact match
    if (resolvedCode !== productCode) {
        const exact = items.find((e) => e.lyvesCode === resolvedCode);
        if (exact) return exact;
    }

    // Fallback 1: key + columnId at/after wildcardIndex
    let found = items.find((e) => {
        if (typeof e.lyvesCode !== "string") return false;
        if (e.lyvesCode.length !== productCode.length) return false;
        const idxKey = e.lyvesCode.indexOf(key);
        const idxCol = e.lyvesCode.indexOf(columnId);
        return idxKey >= wildcardIndex && idxCol >= wildcardIndex;
    });
    if (found) return found;

    // Fallback 2: key only
    found = items.find((e) => {
        if (typeof e.lyvesCode !== "string") return false;
        if (e.lyvesCode.length !== productCode.length) return false;
        if (productCode.includes("#")) return false;
        return e.lyvesCode.indexOf(key) >= wildcardIndex;
    });
    if (found) return found;

    // Fallback 3: columnId only
    found = items.find((e) => {
        if (typeof e.lyvesCode !== "string") return false;
        if (e.lyvesCode.length !== productCode.length) return false;
        if (productCode.includes("*")) return false;
        return e.lyvesCode.indexOf(columnId) >= wildcardIndex;
    });
    if (found) return found;

    return null;
}

export function hasMissingDynamicCodes(matchedItems, keys, wz_columns, productCode) {
    for (let key of keys) {
        for (let column of wz_columns) {
            const colId = column.id;

            const directValue =
                matchedItems[key] !== undefined && matchedItems[key][colId] !== undefined
                    ? matchedItems[key][colId]
                    : undefined;

            const matchedArray = Object.values(matchedItems);
            const bestMatch = findBestMatch(matchedArray, key, colId, productCode);
            const inherit_value = bestMatch?.dynamicsCode || "";

            if (keys.length > 0 && colId === "Key") {
                continue;
            }

            if (directValue === undefined && !inherit_value) {
                return true;
            }
        }
    }

    return false;
}

export function getPRNCodeText(valueArray, matchedItems, missingCodes) {
    if (!valueArray || valueArray.length === 0) {
        return "🔴";
    }

    if (valueArray.length === 1) {
        return valueArray[0]?.dynamicsCode;
    }

    if (missingCodes) {
        return "Missing Codes ⚠️";
    }

    const codes = matchedItems.map((item) => item.dynamicsCode).filter((code) => code);
    const hasDuplicate = new Set(codes).size !== codes.length;
    return hasDuplicate ? "PRN Codes ✔️ 🔁" : "PRN Codes ✔️";
}

export function getGroupHeaders(columns, classes) {
    const grouping_headers = [];
    let last_grouping;
    let num_in_this_group = -1;
    let header_i = 0;

    columns.forEach((column) => {
        if (column.hideInTable) return;
        const this_grouping = column.grouping;

        if (
            this_grouping !== last_grouping ||
            (this_grouping === undefined && last_grouping) ||
            num_in_this_group > 8
        ) {
            const cell_style = {};
            if (header_i % 2 === 0) cell_style.background = "#936a2b !important";

            grouping_headers.push(
                <TableCell
                    key={column.id + "_groupheader"}
                    className={
                        (last_grouping ? classes.categoryHeadingCell : classes.categoryHeadingCellBlank) +
                        " " +
                        classes.headingCell
                    }
                    style={cell_style}
                    colSpan={num_in_this_group + 1}
                >
                    {last_grouping || ""}
                </TableCell>
            );
            header_i++;
            last_grouping = this_grouping;
            num_in_this_group = 0;
        } else {
            num_in_this_group++;
        }
    });

    if (num_in_this_group >= 0) {
        const cell_style = {};
        if (header_i % 2 === 0) cell_style.background = "#936a2b !important";

        grouping_headers.push(
            <TableCell
                key={"beam_col_end"}
                className={
                    (last_grouping ? classes.categoryHeadingCell : classes.categoryHeadingCellBlank) +
                    " " +
                    classes.headingCell
                }
                style={cell_style}
                colSpan={num_in_this_group + 1}
            >
                {last_grouping || ""}
            </TableCell>
        );
    }

    return grouping_headers;
}
